import React, { useMemo, useState } from 'react';
import { ArrowLeft, Info, ChevronLeft, ChevronRight, Snowflake, GitBranch } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import {
  Container, NavOverlay, NavButton, TooltipBox,
  EdgeArrow, PivotCounter, ZoomControlsPanel, ZoomButton, ZoomLevel,
  SvgDefs, useZoomToFit, getAdaptiveTextParams,
  DEFAULT_LINE_WIDTH, wrapText, getScenarioColor,
  buildGradientStops, estimateLabelHeight,
} from './branchViewShared';

// --- Main Component (Radial / "Snowflake" layout) ---

export default function PivotPointBranchView({
  pivotPoint,
  memberNodes,
  scenarios,
  width,
  height,
  onBack,
  onInfoClick,
  onMemberClick,
  currentIndex,
  totalCount,
  onPrev,
  onNext,
  layoutMode,
  onToggleLayout,
}) {
  const { t } = useTranslation();
  const [hoveredNodeId, setHoveredNodeId] = useState(null);
  const [tooltip, setTooltip] = useState(null);

  // Build a scenario lookup by id
  const scenarioById = useMemo(() => {
    const map = {};
    (scenarios || []).forEach(s => { map[s.id] = s; });
    return map;
  }, [scenarios]);

  // Group members by scenario
  const scenarioGroups = useMemo(() => {
    const groups = new Map();
    (memberNodes || []).forEach(member => {
      const sId = member.scenarioId || 'unknown';
      if (!groups.has(sId)) {
        const scenario = scenarioById[sId];
        groups.set(sId, {
          scenarioId: sId,
          title: scenario?.title || sId,
          color: scenario?.color || getScenarioColor(scenario?.title) || '#607d8b',
          members: [],
        });
      }
      groups.get(sId).members.push(member);
    });
    return Array.from(groups.values());
  }, [memberNodes, scenarioById]);

  // Layout calculations
  const layout = useMemo(() => {
    const cx = width / 2;
    const cy = height / 2;
    const minDim = Math.min(width, height);

    const branchRadius = minDim * 0.22;
    const baseLeafRadius = minDim * 0.42;
    const groupCount = scenarioGroups.length;
    const centerRadius = Math.max(32, minDim * 0.045);
    const branchNodeRadius = Math.max(20, minDim * 0.025);
    const leafNodeRadius = Math.max(8, minDim * 0.012);

    const branches = scenarioGroups.map((group, idx) => {
      const angle = groupCount === 1
        ? -Math.PI / 2
        : (idx / groupCount) * 2 * Math.PI - Math.PI / 2;

      const bx = cx + Math.cos(angle) * branchRadius;
      const by = cy + Math.sin(angle) * branchRadius;

      const memberCount = group.members.length;
      const maxFanAngle = groupCount === 1
        ? Math.PI * 1.6
        : ((2 * Math.PI) / groupCount) * 0.75;

      const maxLabelH = Math.max(
        ...group.members.map(m => estimateLabelHeight(m.text)),
        40
      );
      const minAngularStep = maxLabelH / baseLeafRadius;
      const neededAngle = memberCount > 1 ? minAngularStep * (memberCount - 1) : 0;

      let leafRadius = baseLeafRadius;
      let usedFanAngle = maxFanAngle;

      if (neededAngle > maxFanAngle && memberCount > 1) {
        leafRadius = (maxLabelH * (memberCount - 1)) / maxFanAngle;
        leafRadius = Math.min(leafRadius, minDim * 0.8);
      } else {
        usedFanAngle = neededAngle > 0 ? neededAngle : maxFanAngle;
      }

      const angleStep = memberCount > 1 ? usedFanAngle / (memberCount - 1) : 0;
      const startAngle = angle - usedFanAngle / 2;
      // 3-level stagger for dense groups: near / mid / far from center
      const staggerLevels = memberCount > 15 ? 3 : memberCount > 3 ? 2 : 1;
      const staggerOffset = leafRadius * 0.25;

      const leaves = group.members.map((member, mIdx) => {
        const leafAngle = memberCount === 1 ? angle : startAngle + mIdx * angleStep;
        const level = mIdx % staggerLevels;
        const r = leafRadius + level * staggerOffset;
        return {
          ...member,
          x: cx + Math.cos(leafAngle) * r,
          y: cy + Math.sin(leafAngle) * r,
          nodeRadius: leafNodeRadius,
        };
      });

      return { ...group, x: bx, y: by, angle, nodeRadius: branchNodeRadius, leaves };
    });

    return { cx, cy, centerRadius, branchRadius, leafRadius: baseLeafRadius, branches };
  }, [width, height, scenarioGroups]);

  // Center gradient
  const centerGradientStops = useMemo(() => {
    const names = pivotPoint?.scenarioNames || [];
    return names.length >= 2 ? buildGradientStops(names) : null;
  }, [pivotPoint]);

  const centerFill = useMemo(() => {
    if (centerGradientStops) return 'url(#center-gradient)';
    const names = pivotPoint?.scenarioNames || [];
    if (names.length === 1) return getScenarioColor(names[0]);
    return '#9C27B0';
  }, [pivotPoint, centerGradientStops]);

  // Zoom
  const { svgRef, zoomLevel, handleZoomIn, handleZoomOut, handleZoomReset, zoomToPoints } =
    useZoomToFit({ pivotId: pivotPoint?.id, layout, width, height });

  // Adaptive text params based on total leaf count and zoom
  const totalLeaves = layout.branches.reduce((s, b) => s + b.leaves.length, 0);
  const tp = getAdaptiveTextParams(totalLeaves, zoomLevel);

  // Hover
  const isHighlighted = (nodeId) => !hoveredNodeId || nodeId === hoveredNodeId;
  const dimOpacity = hoveredNodeId ? 0.15 : 1;

  const handleMouseEnter = (node, event) => {
    setHoveredNodeId(node.id);
    const scenarioName = scenarioById[node.scenarioId]?.title || '';
    setTooltip({
      x: event.clientX + 12, y: event.clientY - 10,
      title: node.text || node.title || '',
      scenarioName, scenarioColor: getScenarioColor(scenarioName),
      participant: node.participant,
    });
  };
  const handleMouseLeave = () => { setHoveredNodeId(null); setTooltip(null); };

  return (
    <Container>
      <NavOverlay>
        {onBack && (
          <NavButton onClick={onBack}><ArrowLeft /> {t('pivotPoints.common.back')}</NavButton>
        )}
        <NavButton onClick={onInfoClick}><Info /> {t('pivotPoints.common.info')}</NavButton>
        {onToggleLayout && (
          <NavButton onClick={onToggleLayout} title={t('pivotPoints.branch.treeTitle')}>
            <GitBranch /> {t('pivotPoints.branch.tree')}
          </NavButton>
        )}
      </NavOverlay>

      {totalCount > 1 && (
        <>
          <EdgeArrow $side="left" onClick={onPrev} disabled={currentIndex <= 0} title={t('pivotPoints.common.previousPivot')}>
            <ChevronLeft />
          </EdgeArrow>
          <EdgeArrow $side="right" onClick={onNext} disabled={currentIndex >= totalCount - 1} title={t('pivotPoints.common.nextPivot')}>
            <ChevronRight />
          </EdgeArrow>
          <PivotCounter>{currentIndex + 1} / {totalCount}</PivotCounter>
        </>
      )}

      <svg ref={svgRef} width={width} height={height}>
        <SvgDefs centerGradientStops={centerGradientStops} />
        <g className="zoomable-content">

        {/* Connection lines: center → branches */}
        {layout.branches.map((branch, i) => (
          <line key={`cb-${i}`} x1={layout.cx} y1={layout.cy} x2={branch.x} y2={branch.y}
            stroke={branch.color} strokeWidth={2} strokeOpacity={hoveredNodeId ? 0.1 : 0.3} />
        ))}

        {/* Connection lines: branches → leaves */}
        {layout.branches.map((branch, bi) =>
          branch.leaves.map((leaf, li) => (
            <line key={`bl-${bi}-${li}`} x1={branch.x} y1={branch.y} x2={leaf.x} y2={leaf.y}
              stroke={branch.color} strokeWidth={1.5}
              strokeOpacity={isHighlighted(leaf.id) ? 0.4 : dimOpacity * 0.2}
              style={{ transition: 'stroke-opacity 0.2s ease' }} />
          ))
        )}

        {/* Leaf nodes — adaptive text based on density and zoom */}
        {layout.branches.map((branch, bi) =>
          branch.leaves.map((leaf, li) => {
            const highlighted = isHighlighted(leaf.id);
            const { lines } = wrapText(leaf.text || '', tp.labelWidth, tp.maxLabelLines);
            const labelY = leaf.y + leaf.nodeRadius + 14;
            return (
              <g key={`l-${bi}-${li}`} style={{ cursor: 'pointer', transition: 'opacity 0.2s ease' }}
                opacity={highlighted ? 1 : dimOpacity}
                onMouseEnter={(e) => handleMouseEnter(leaf, e.nativeEvent)}
                onMouseLeave={handleMouseLeave}
                onClick={() => onMemberClick?.(leaf)}>
                <circle cx={leaf.x} cy={leaf.y} r={leaf.nodeRadius}
                  fill={branch.color} fillOpacity={0.7}
                  stroke={highlighted && hoveredNodeId ? 'white' : 'rgba(255,255,255,0.3)'}
                  strokeWidth={highlighted && hoveredNodeId ? 1.5 : 0.5}
                  filter={highlighted && hoveredNodeId ? 'url(#leaf-glow)' : undefined} />
                {tp.showLeafText && (
                  <text x={leaf.x} y={labelY} textAnchor="middle" fill="rgba(255,255,255,0.85)" fontSize={`${tp.leafFontSize}px`} fontWeight="500">
                    {lines.map((line, idx) => (
                      <tspan key={idx} x={leaf.x} dy={idx === 0 ? 0 : tp.lineHeight}>{line}</tspan>
                    ))}
                  </text>
                )}
              </g>
            );
          })
        )}

        {/* Scenario branch nodes — click to zoom to this branch's leaves */}
        {layout.branches.map((branch, i) => {
          const { lines } = wrapText(branch.title, 16, 2);
          const labelY = branch.y + branch.nodeRadius + 16;
          const handleBranchClick = () => {
            const points = [
              { x: branch.x - 30, y: branch.y - 30 },
              { x: branch.x + 30, y: branch.y + 30 },
              ...branch.leaves.map(l => ({ x: l.x - 60, y: l.y - 20 })),
              ...branch.leaves.map(l => ({ x: l.x + 60, y: l.y + 40 })),
            ];
            zoomToPoints(points, `branch-${i}`);
          };
          return (
            <g key={`br-${i}`} style={{ cursor: 'pointer' }} onClick={handleBranchClick}>
              <circle cx={branch.x} cy={branch.y} r={branch.nodeRadius}
                fill={branch.color} fillOpacity={0.85} stroke="rgba(255,255,255,0.4)" strokeWidth={1.5} />
              <text x={branch.x} y={branch.y} textAnchor="middle" dominantBaseline="central"
                fill="white" fontSize={Math.max(11, branch.nodeRadius * 0.55) + 'px'} fontWeight="700">
                {branch.members.length}
              </text>
              <text x={branch.x} y={labelY} textAnchor="middle" fill="white" fontSize="11px" fontWeight="600">
                {lines.map((line, idx) => (
                  <tspan key={idx} x={branch.x} dy={idx === 0 ? 0 : 13}>{line}</tspan>
                ))}
              </text>
            </g>
          );
        })}

        {/* Center node */}
        <g style={{ cursor: 'pointer' }} onClick={onInfoClick} filter="url(#center-glow)">
          <circle cx={layout.cx} cy={layout.cy} r={layout.centerRadius}
            fill={centerFill} fillOpacity={0.9} stroke="rgba(255,255,255,0.6)" strokeWidth={2} />
          <text x={layout.cx} y={layout.cy} textAnchor="middle" dominantBaseline="central"
            fill="white" fontSize={Math.max(14, layout.centerRadius * 0.45) + 'px'} fontWeight="700">
            {memberNodes?.length || 0}
          </text>
          {(() => {
            const { lines: titleLines } = wrapText(pivotPoint?.text || pivotPoint?.title || '', 22, 4);
            const startY = layout.cy + layout.centerRadius + 18;
            return (
              <text x={layout.cx} y={startY} textAnchor="middle" fill="white" fontSize="13px" fontWeight="700">
                {titleLines.map((line, idx) => (
                  <tspan key={idx} x={layout.cx} dy={idx === 0 ? 0 : 15}>{line}</tspan>
                ))}
              </text>
            );
          })()}
        </g>

        </g>
      </svg>

      <ZoomControlsPanel>
        <ZoomButton onClick={handleZoomIn} title={t('pivotPoints.common.zoomIn')}>+</ZoomButton>
        <ZoomLevel>{Math.round(zoomLevel * 100)}%</ZoomLevel>
        <ZoomButton onClick={handleZoomOut} title={t('pivotPoints.common.zoomOut')}>−</ZoomButton>
        <ZoomButton onClick={handleZoomReset} title={t('pivotPoints.common.zoomReset')} style={{ fontSize: '0.9rem' }}>⟲</ZoomButton>
      </ZoomControlsPanel>

      {tooltip && (
        <TooltipBox style={{ left: tooltip.x, top: tooltip.y }}>
          <div className="tooltip-title">{tooltip.title}</div>
          {tooltip.scenarioName && (
            <span className="tooltip-scenario" style={{
              background: `${tooltip.scenarioColor}33`, color: tooltip.scenarioColor,
              border: `1px solid ${tooltip.scenarioColor}66`,
            }}>{tooltip.scenarioName}</span>
          )}
          {tooltip.participant && <div className="tooltip-meta">{tooltip.participant}</div>}
        </TooltipBox>
      )}
    </Container>
  );
}
